import { ReactComponent as Export } from '$icons/export.svg';
import { ReactComponent as Arrow } from '$icons/ArrowBackInternal.svg';
import { SEARCH_CANDIDATES_SHORTDATE } from '$constants/datetimes';
import {
  useLazySearchCandidateQuery,
  SearchCandidateInput,
  SearchCandidateOutput,
} from '$store/modules/candidates';
import dayjs from 'dayjs';
import { useMemo, useCallback, ChangeEvent } from 'react';

const SearchCandidatesTableNavigation = ({
  searchCandidateData,
  pageSize,
  setPageSize,
  selectedRows,
  columnSort,
  setIsGeneratingCSV,
  page,
  setPage,
  searchCandidatesFilters,
  openAddPositionHandler,
}: {
  searchCandidateData: SearchCandidateOutput | undefined;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  selectedRows: any;
  columnSort: any;
  setIsGeneratingCSV: (isGenerating: boolean) => void;
  page: number;
  setPage: (pageNum: any) => void;
  searchCandidatesFilters: any;
  openAddPositionHandler: any;
}) => {
  // Limit set on the backend side
  const SearchCandidatesMaxResultSize = 125;
  const [searchCandidate] = useLazySearchCandidateQuery();

  const totalPages = useMemo(
    () => Math.ceil((searchCandidateData?.total || 0) / pageSize),
    [searchCandidateData?.total, pageSize]
  );

  const exportCandidates = useCallback(async () => {
    let allSearchData;
    const selectedCandidates = Object.keys(selectedRows);

    // if already has fetched all the data or has selected only some rows
    if (
      searchCandidateData!.total <= searchCandidateData!.data.length ||
      selectedCandidates.length > 0
    ) {
      // If user hasnt selected any candidate, let him export all the candidates
      if (selectedCandidates.length === 0) {
        allSearchData = searchCandidateData?.data;
      } else {
        // Otherwise, only export the selected candidates
        allSearchData = searchCandidateData?.data?.filter(
          (singleCandidate: any) => {
            return selectedCandidates.indexOf(`${singleCandidate.Id}`) >= 0;
          }
        );
      }
    } else {
      setIsGeneratingCSV(true);

      const allSearchTotalPages = Math.ceil(
        searchCandidateData!.total / SearchCandidatesMaxResultSize
      );

      const allSearchDataPromises = [];

      for (
        let searchPage = 1;
        searchPage <= allSearchTotalPages;
        searchPage += 1
      ) {
        allSearchDataPromises.push(
          searchCandidate({
            ...searchCandidatesFilters,
            orderBy: columnSort.column,
            Order: columnSort.sortType,
            page: searchPage,
            take: SearchCandidatesMaxResultSize,
          } as SearchCandidateInput)
        );
      }
      allSearchData = await Promise.all(allSearchDataPromises)
        .then((resp) => resp.map((singleResp) => singleResp!.data!.data))
        .then((resp) => resp.flat());

      setIsGeneratingCSV(false);
    }

    // If for some reason the endpoint doesnt return data dont download an empty csv
    if (!allSearchData) return;

    const csvHeaders = [
      'Candidate Id',
      'Email',
      'Name',
      'Last Name',
      `Create Date (${SEARCH_CANDIDATES_SHORTDATE})`,
      `Last Login (${SEARCH_CANDIDATES_SHORTDATE})`,
      'Country',
      `Last Job`,
      'Salary Range',
      'CV',
      'Video',
      'Reference',
      'Quality Status',
      'Open To Work',
      'Open To Work Last Update',
      'Skills',
      'Work History',
      'Education',
      'Job Titles',
    ].join(',');

    const csvContent =
      allSearchData!.map((singleCandidate: any) => {
        const candidateData: string[] = [];
        /* Candidate Id */
        candidateData.push(`${singleCandidate.Id}`);

        /* Candidate Email */
        candidateData.push(`"${singleCandidate.User?.Email}"`);

        /* Candidate Name */
        candidateData.push(`"${singleCandidate.User?.FirstName}"`);

        /* Candidate Last Name */
        candidateData.push(`"${singleCandidate.User?.LastName}"`);

        /* Create Date */
        candidateData.push(
          `"${dayjs(singleCandidate.CreateTime).format(
            SEARCH_CANDIDATES_SHORTDATE
          )}"`
        );

        /* Last Login */
        candidateData.push(
          singleCandidate.LastLogin
            ? `"${dayjs(singleCandidate.LastLogin).format(
                SEARCH_CANDIDATES_SHORTDATE
              )}"`
            : 'N/A'
        );

        /* Country */
        candidateData.push(
          `${
            singleCandidate.Country && 'Name' in singleCandidate.Country
              ? singleCandidate.Country.Name
              : 'N/A'
          }`
        );

        /* Last Job */
        candidateData.push(
          `${
            singleCandidate.CandidateWorkExperience &&
            singleCandidate.CandidateWorkExperience.length > 0 &&
            singleCandidate.CandidateWorkExperience[0].JobPosition &&
            singleCandidate.CandidateWorkExperience[0].JobPosition.title
              ? singleCandidate.CandidateWorkExperience[0].JobPosition.title.replace(
                  /(\r|\t|\n)/gi,
                  ''
                )
              : 'N/A'
          }`
        );

        /* Candidate Salary Range */
        if (
          !singleCandidate.CandidateJobInterest ||
          (!singleCandidate.CandidateJobInterest?.SalaryLowerBound &&
            !singleCandidate.CandidateJobInterest?.SalaryUpperBound)
        )
          candidateData.push('N/A');
        else
          candidateData.push(
            `${
              singleCandidate.CandidateJobInterest.SalaryLowerBound || 'N/A'
            } - ${
              singleCandidate.CandidateJobInterest.SalaryUpperBound || 'N/A'
            }`
          );

        /* CV */
        candidateData.push(singleCandidate.Curriculum ? 'TRUE' : 'FALSE');

        /* Video */
        candidateData.push(
          singleCandidate.AdditionalVideos &&
            singleCandidate.AdditionalVideos.length > 0
            ? 'TRUE'
            : 'FALSE'
        );

        /* Reference */
        candidateData.push(
          singleCandidate.Files &&
            singleCandidate.Files.length > 0 &&
            singleCandidate.Files.find(
              (singleFile: any) =>
                singleFile?.FileCategory?.CategoryName?.toLowerCase() ===
                'reference'
            )
            ? 'TRUE'
            : 'FALSE'
        );

        /* Quality Status */
        candidateData.push(singleCandidate.QualityStatus || 'N/A');

        /* Open To Work */
        candidateData.push(singleCandidate.OpenToWork || 'N/A');

        /* Open To Work Last Update */
        candidateData.push(
          singleCandidate.OpenToWorkUpdatedOn
            ? `"${dayjs(singleCandidate.OpenToWorkUpdatedOn).format(
                SEARCH_CANDIDATES_SHORTDATE
              )}"`
            : 'N/A'
        );

        /* Skills */
        candidateData.push(
          `"${singleCandidate?.CandidateSkills?.map(
            (singleSkill: any) => singleSkill.Skill.Title
          )
            ?.join(', ')
            ?.replace(/(\r|\t|\n)/gi, '')}"`
        );

        /* Work History */
        candidateData.push(
          `"${singleCandidate?.CandidateWorkExperience?.map(
            (singleExp: any) => `${singleExp.JobPosition?.title}`
          )
            ?.join(', ')
            ?.replace(/(\r|\t|\n)/gi, '')}"`
        );

        /* Education */
        candidateData.push(
          `"${singleCandidate?.CandidateEducation?.map(
            (singleEducation: any) => singleEducation.EducationalInstitutionName
          )
            ?.join(', ')
            ?.replace(/(\r|\t|\n)/gi, '')}"`
        );

        /* Job Titles */
        candidateData.push(
          `"${singleCandidate?.CandidateWorkExperience?.map(
            (singleExp: any) => `${singleExp.JobTitle}`
          )
            ?.join(', ')
            ?.replace(/(\r|\t|\n)/gi, '')}"`
        );

        return candidateData.join(',');
      }) || [];

    const blob = new Blob([[csvHeaders, ...csvContent].join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });

    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = 'Simera - Candidates.csv';

    link.click();

    URL.revokeObjectURL(blobUrl);
  }, [
    selectedRows,
    searchCandidateData,
    setIsGeneratingCSV,
    searchCandidate,
    searchCandidatesFilters,
    columnSort.column,
    columnSort.sortType,
  ]);

  const handleChangeAmmount = (ev: ChangeEvent<HTMLSelectElement>) => {
    setPageSize(parseInt(ev.target.value, 10));
    setPage(1);
  };

  const hasSelectedCandidates = useMemo(() => {
    return Object.values(selectedRows).find((value) => value === true);
  }, [selectedRows]);

  return (
    <div className="ml-auto z-10 sticky bottom-0 items-center flex gap-8 bg-[#f1f3f6] px-4 rounded">
      <div className="flex items-center gap-4">
        <button
          type="button"
          disabled={page === 1}
          className="disabled:cursor-not-allowed disabled:opacity-25"
          onClick={() => setPage((prev: number) => prev - 1)}
        >
          <Arrow className="fill-zinc-600 w-8 h-8" />
        </button>
        {page} of {totalPages}
        <button
          type="button"
          disabled={page === totalPages || totalPages === 0}
          className="disabled:cursor-not-allowed disabled:opacity-25"
          onClick={() => setPage((prev: number) => prev + 1)}
        >
          <Arrow className="fill-zinc-600 w-8 h-8 rotate-180" />
        </button>
      </div>
      <label htmlFor="showCandidatesNumber" className="gap-2 flex items-center">
        Show
        <select
          id="showCandidatesNumber"
          onChange={handleChangeAmmount}
          defaultValue={100}
          className="p-2"
        >
          <option value="20">20</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </select>
      </label>
      <div className="flex flex-row gap-4">
        <button
          type="button"
          className="group bg-internal-main p-2 flex items-center overflow-hidden transition-all duration-300 ease-in-out"
          onClick={exportCandidates}
          title="Download CSV"
        >
          <Export className="w-6 h-6 fill-white rotate-180 flex-shrink-0" />
          <span className="max-w-0 group-hover:max-w-xs overflow-hidden transition-all duration-300 ease-in-out whitespace-nowrap text-white group-hover:ml-2">
            Download as CSV
          </span>
        </button>
        <button
          type="button"
          className="group disabled:bg-gray-400 disabled:cursor-not-allowed bg-internal-main p-2 flex flex-row items-center overflow-hidden transition-all duration-300 ease-in-out"
          onClick={openAddPositionHandler}
          title="Add Candidates to a position"
          disabled={!hasSelectedCandidates}
        >
          <i className="w-6 h-6 fill-white rotate-180 flex-shrink-0 pi pi-plus text-white font-extrabold flex items-center justify-center" />
          <span className="max-w-0 group-hover:max-w-xs overflow-hidden transition-all duration-300 ease-in-out whitespace-nowrap text-white group-hover:ml-2 flex items-center h-6">
            Adds selected candidates to a position
          </span>
        </button>
      </div>
    </div>
  );
};

export default SearchCandidatesTableNavigation;
